import { motion } from "framer-motion";
import { Reveal, Chapter, CapsuleBar, Code } from "./shared";
import { PersonCard, people } from "./phone/PeopleScreen";

const nowBars = [
  { label: "MOMENTUM", value: 72, color: "#FF4D0A" },
  { label: "TENSION", value: 34, color: "#F2F0EB" },
  { label: "CLARITY", value: 81, color: "#9DA1A8" },
  { label: "POTENTIAL", value: 64, color: "#6E7178" },
];

const curveVariants = {
  hide: { pathLength: 0 },
  show: { pathLength: 1, transition: { duration: 1.6, ease: "easeOut" } },
};
const dotVariants = (delay) => ({
  hide: { opacity: 0, scale: 0 },
  show: { opacity: 1, scale: 1, transition: { delay, duration: 0.4 } },
});

const EnergyCurve = () => (
  <motion.div
    initial="hide"
    whileInView="show"
    viewport={{ once: true, amount: 0.4 }}
    className="hairline bg-[#0F1013] p-4 sm:p-6 w-full"
    data-testid="energy-curve-chart"
  >
    <div className="flex items-center gap-2.5 mb-4">
      <span className="w-5 h-[2px] bg-[#F2F0EB] inline-block" />
      <span className="font-mono-brand text-[9px] tracking-[0.3em] text-[#F2F0EB]">HOW YOUR ENERGY IS MOVING</span>
    </div>
    <svg viewBox="0 0 400 116" className="w-full h-auto" fill="none" aria-hidden="true">
      <motion.line x1="330" y1="14" x2="330" y2="102" stroke="#3A3C40" strokeWidth="1" variants={dotVariants(1.2)} />
      <motion.path
        d="M 8 46 C 28 38, 46 32, 68 32 C 120 33, 180 68, 244 84 C 274 91, 300 89, 330 85 C 352 82, 376 79, 394 78"
        stroke="#F2F0EB"
        strokeWidth="2.5"
        strokeLinecap="round"
        variants={curveVariants}
      />
      <motion.circle cx="68" cy="32" r="5" fill="#F2F0EB" variants={dotVariants(0.5)} />
      <motion.circle cx="244" cy="84" r="4" stroke="#9DA1A8" strokeWidth="1.5" fill="#0F1013" variants={dotVariants(0.9)} />
      <motion.circle cx="330" cy="85" r="4.5" fill="#FF4D0A" variants={dotVariants(1.3)} />
    </svg>
    <div className="flex justify-between font-mono-brand text-[9px] tracking-[0.2em] text-[#9DA1A8] mt-1">
      <span>6 AM</span>
      <span>2 PM</span>
      <span>10 PM</span>
    </div>
    <p className="font-mono-brand text-[9px] tracking-[0.15em] mt-4">
      <span className="text-[#F2F0EB]">HIGHEST ENERGY 8–10 AM</span>
      <span className="text-[#6E7178]"> · LOWEST AROUND 6 PM</span>
    </p>
  </motion.div>
);

const Card = ({ children, className = "", testId, delay = 0 }) => (
  <Reveal delay={delay} className={`card-line bg-[#141518] p-6 sm:p-8 lg:p-10 ${className}`} data-testid={testId}>
    {children}
  </Reveal>
);

export const Pillars = () => (
  <section id="instrument" className="py-16 sm:py-24 lg:py-32 bg-[#0B0B0C] border-y border-[#F2F0EB]/5" data-testid="pillars-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <Chapter num="03" label="What's inside" />
      </Reveal>
      <Reveal>
        <h2 className="h-heavy text-[32px] sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl mb-10 sm:mb-16" data-testid="pillars-headline">
          Everything you need
          <br />
          <span className="text-[#6E7178]">to read your day.</span>
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <Card className="md:col-span-7" testId="pillar-people">
          <p className="eyebrow mb-3 !text-[#FF4D0A]">People</p>
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">How they're doing. How you're doing. One screen.</h3>
          <p className="text-[#9DA1A8] leading-relaxed mb-8 max-w-md">
            Add your boss, your partner, your clients. Know the room before
            you walk into it.
          </p>
          <div className="space-y-3 max-w-md">
            {people.map((p, i) => (
              <PersonCard key={p.rel} p={p} delay={0.2 + i * 0.15} compact />
            ))}
          </div>
        </Card>

        <Card className="md:col-span-5" testId="pillar-ask" delay={0.1}>
          <p className="eyebrow mb-3 !text-[#FF4D0A]">Ask</p>
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">Ask a real question. Get a clear call.</h3>
          <p className="text-[#9DA1A8] leading-relaxed mb-8">
            &ldquo;Should I bring it up tonight, or let it breathe?&rdquo; A real
            answer: the window, the friction, the move.
          </p>
          <div className="hairline bg-[#1A1C20] p-4">
            <p className="font-mono-brand text-[9px] tracking-[0.3em] text-[#FF4D0A] mb-2">CLEAR CALL</p>
            <p className="text-sm text-[#F2F0EB] leading-relaxed">
              Hold until Thursday. Your window opens 09:00. Exhale.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Code code="VS" color="#FF4D0A" />
              <span className="font-mono-brand text-[9px] tracking-[0.15em] text-[#9DA1A8]">THU 09:00–13:00</span>
            </div>
          </div>
        </Card>

        <Card className="md:col-span-5" testId="pillar-now" delay={0.05}>
          <p className="eyebrow mb-3 !text-[#FF4D0A]">Now</p>
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">How your energy is moving today.</h3>
          <p className="text-[#9DA1A8] leading-relaxed mb-8">
            One honest morning read, before the day starts deciding for you.
          </p>
          <div className="space-y-4">
            {nowBars.map((b, i) => (
              <div key={b.label}>
                <div className="flex justify-between mb-1.5">
                  <span className="font-mono-brand text-[10px] tracking-[0.2em] text-[#9DA1A8]">{b.label}</span>
                  <span className="font-mono-brand text-[10px] text-[#6E7178]">{b.value}</span>
                </div>
                <CapsuleBar value={b.value} color={b.color} delay={0.2 + i * 0.12} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="md:col-span-7" testId="pillar-energy" delay={0.15}>
          <div className="flex flex-col gap-8 h-full">
            <div>
              <p className="eyebrow mb-3 !text-[#FF4D0A]">Energy</p>
              <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">See the dip before it hits.</h3>
              <p className="text-[#9DA1A8] leading-relaxed max-w-md">
                Your energy has a shape every day, a peak, a slide, a recovery.
                The app draws it in advance so the big things land in your high hours.
              </p>
            </div>
            <EnergyCurve />
          </div>
        </Card>
      </div>
    </div>
  </section>
);
